// Mirrors byair-product/features/localization/technical.md. UI resource tags are not request tags.
export const APP_LANGUAGES = [
  'en',
  'cs',
  'de',
  'es',
  'fi',
  'fr',
  'hi',
  'it',
  'ja',
  'ko',
  'pl',
  'pt-BR',
  'pt-PT',
  'ru',
  'sv',
  'th',
  'tr',
  'vi',
  'zh-Hans',
  'zh-Hant'
];
export const AUTO_LOCALE_ROUTE_KEY = 'byair-auto-locale-route';

export const parseLocaleTag = (value) => {
  if (typeof value !== 'string') return null;
  const parts = value.trim().replace(/_/g, '-').split('-');
  if (!/^[a-z]{2,3}$/i.test(parts[0])) return null;
  const language = parts.shift().toLowerCase();
  let script;
  let region;
  if (/^[a-z]{4}$/i.test(parts[0] || '')) {
    const part = parts.shift().toLowerCase();
    script = part[0].toUpperCase() + part.slice(1);
  }
  if (/^(?:[a-z]{2}|\d{3})$/i.test(parts[0] || '')) region = parts.shift().toUpperCase();
  // Extensions are formatter-only; never leak them into a request or profile locale.
  if (parts.length && !/^[a-z0-9]$/i.test(parts[0])) return null;
  if (parts.some((part) => !/^[a-z0-9]{1,8}$/i.test(part))) return null;
  return { language, script, region, tag: [language, script, region].filter(Boolean).join('-') };
};

export const matchAppLanguage = (value) => {
  const locale = parseLocaleTag(value);
  if (!locale) return null;
  if (locale.language === 'zh') {
    if (locale.script === 'Hans') return 'zh-Hans';
    if (locale.script === 'Hant') return 'zh-Hant';
    return ['TW', 'HK', 'MO'].includes(locale.region) ? 'zh-Hant' : 'zh-Hans';
  }
  if (locale.language === 'pt') return locale.region === 'PT' ? 'pt-PT' : 'pt-BR';
  return APP_LANGUAGES.includes(locale.language) ? locale.language : null;
};

export const resolveBrowserAppLanguage = (languages = [], supported = APP_LANGUAGES) => {
  for (const value of languages) {
    const match = matchAppLanguage(value);
    if (match && supported.includes(match)) return match;
  }
  return 'en';
};

// Negotiate only supported UI resource tags. Never use a raw header as a cache
// key. Wildcards/unsupported or malformed preferences fall back to English.
export const resolveAcceptLanguage = (header) => {
  if (typeof header !== 'string' || header.length > 4096) return 'en';
  const preferences = header
    .split(',')
    .slice(0, 64)
    .flatMap((entry, index) => {
      const [tag, ...parameters] = entry.trim().split(';');
      let quality = 1;
      if (parameters.length) {
        if (
          parameters.length !== 1 ||
          !/^\s*q=(?:0(?:\.\d{0,3})?|1(?:\.0{0,3})?)\s*$/i.test(parameters[0])
        )
          return [];
        quality = Number(parameters[0].trim().slice(2));
      }
      const language = matchAppLanguage(tag.trim());
      return language && quality > 0 ? [{ language, quality, index }] : [];
    });
  preferences.sort((a, b) => b.quality - a.quality || a.index - b.index);
  return preferences[0]?.language || 'en';
};

const countryRegion = (value) => (/^[A-Z]{2}$/.test(value || '') ? value : null);

export const resolveLocaleContract = ({
  systemLocale = 'en',
  appLocale,
  deviceRegion,
  isAppLocaleExplicit = false,
  hourCycle
} = {}) => {
  const system = parseLocaleTag(systemLocale);
  const matched = matchAppLanguage(systemLocale);
  const app = matchAppLanguage(appLocale) || matched || 'en';
  const region = countryRegion(deviceRegion?.toUpperCase()) || countryRegion(system?.region);
  const candidate = !isAppLocaleExplicit && matched === app ? system : parseLocaleTag(app);
  const isPortuguese = app.startsWith('pt-');
  const backendLocale = isPortuguese
    ? app
    : [candidate?.language || app, candidate?.script, region].filter(Boolean).join('-');
  const formatLocale = isPortuguese ? app : [app, region].filter(Boolean).join('-');
  const fallbacks = app.startsWith('zh-') ? [app, 'zh', 'en'] : [app, 'en'];
  const tags = [...new Set([backendLocale, ...fallbacks])];
  const acceptLanguage = tags
    .map((tag, index) =>
      index === 0 ? tag : `${tag};q=${isPortuguese ? '0.8' : (1 - index / 10).toFixed(1)}`
    )
    .join(',');
  return {
    systemLocale: system?.tag || null,
    appLocale: app,
    deviceRegion: region,
    backendLocale,
    formatLocale,
    acceptLanguage,
    hourCycle: ['h11', 'h12', 'h23', 'h24'].includes(hourCycle) ? hourCycle : undefined,
    localeSource: isAppLocaleExplicit ? 'explicit' : matched ? 'system' : 'fallback'
  };
};

export const getBrowserLocaleContract = (appLocale) => {
  const systemLocale = typeof navigator === 'undefined' ? 'en' : navigator.language || 'en';
  const path = typeof window === 'undefined' ? '' : window.location.pathname;
  const prefix = path.split('/')[1];
  let explicit = APP_LANGUAGES.includes(prefix);
  try {
    const automatic = JSON.parse(window.sessionStorage.getItem(AUTO_LOCALE_ROUTE_KEY));
    if (
      typeof automatic?.path === 'string' &&
      automatic.path.replace(/\/+$/, '') === path.replace(/\/+$/, '') &&
      automatic.systemLocale === systemLocale
    )
      explicit = false;
  } catch {
    // A prefixed URL still provides a valid, full locale when session storage is unavailable.
  }
  const effectiveAppLocale = appLocale || (explicit ? prefix : matchAppLanguage(systemLocale));
  let hourCycle = systemLocale.match(/-u-(?:.*-)?hc-(h11|h12|h23|h24)(?:-|$)/i)?.[1];
  try {
    hourCycle ||= new Intl.DateTimeFormat(undefined, { hour: 'numeric' }).resolvedOptions()
      .hourCycle;
  } catch {
    // Old engines can still format clocks using the format locale's default cycle.
  }
  return resolveLocaleContract({
    systemLocale,
    appLocale: effectiveAppLocale,
    isAppLocaleExplicit: explicit,
    hourCycle
  });
};
